import { Button } from "@/components/ui/button";
import { useAdminPerms } from "@/hooks/auth";
import { PlayerModalRefType, useClosePlayerModal } from "@/hooks/playerModal";
import { Loader2Icon } from "lucide-react";
import { useRef, useState } from "react";
import { useBackendApi } from "@/hooks/fetch";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import type { BanTemplatesDataType } from "@shared/otherTypes";
import type { PlayerHistoryItem } from "@shared/playerApiTypes";
import BanForm, { BanFormType } from "@/components/BanForm";
import { txToast } from "@/components/TxToaster";
import { ModalTabInner, ModalTabMessage } from "@/components/modal-tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { STAFF_NOTE_LABEL } from "@/components/StaffNoteText";


type PlayerBanTabProps = {
    banTemplates: BanTemplatesDataType[];
    playerRef: PlayerModalRefType;
    history: PlayerHistoryItem[];
    serverTime: number;
    windowDays: number;
};

export default function PlayerBanTab({ playerRef, banTemplates, history, serverTime, windowDays }: PlayerBanTabProps) {
    const banFormRef = useRef<BanFormType>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isWarn, setIsWarn] = useState(false);
    const [note, setNote] = useState('');
    const { hasPerm } = useAdminPerms();
    const closeModal = useClosePlayerModal();
    const playerBanApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/ban`,
        throwGenericErrors: true,
    });
    const playerWarnApi = useBackendApi<GenericApiOkResp>({
        method: 'POST',
        path: `/player/warn`,
        throwGenericErrors: true,
    });

    if (!hasPerm('players.ban')) {
        return <ModalTabMessage>
            You don't have permission to ban players.
        </ModalTabMessage>;
    }

    const handleSave = () => {
        if (!banFormRef.current) return;
        const { reason, duration, presetId } = banFormRef.current.getData();

        if (!reason || reason.length < 3) {
            txToast.warning(`The reason must be at least 3 characters long.`);
            banFormRef.current.focusReason();
            return;
        }

        setIsSaving(true);
        //A preset's ladder can call for a warn, so this tab can warn too
        const sendAction = duration === 'warn' ? playerWarnApi : playerBanApi;
        sendAction({
            queryParams: playerRef,
            data: { reason, duration, note: note.trim() || undefined, presetId },
            toastLoadingMessage: duration === 'warn' ? 'Warning player...' : 'Banning player...',
            genericHandler: {
                successMsg: duration === 'warn' ? 'Player warned.' : 'Player banned.',
            },
            finally: () => setIsSaving(false),
            success: () => closeModal(),
        });
    };

    return (
        <ModalTabInner className="grid gap-4">
            <BanForm
                ref={banFormRef}
                banTemplates={banTemplates}
                disabled={isSaving}
                onNavigateAway={() => { closeModal(); }}
                ladder={{ history, serverTime, windowDays, canWarn: hasPerm('players.warn') }}
                onDurationChange={(duration) => setIsWarn(duration === 'warn')}
            />
            <div className="grid gap-1">
                <Label htmlFor="banStaffNote">{STAFF_NOTE_LABEL}</Label>
                <Textarea
                    id="banStaffNote"
                    className="resize-y"
                    rows={2}
                    maxLength={4096}
                    disabled={isSaving}
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                />
            </div>
            <div className="flex place-content-end">
                <Button
                    size="sm"
                    variant="destructive"
                    disabled={isSaving}
                    onClick={handleSave}
                >
                    {isSaving ? (
                        <span className="flex items-center leading-relaxed">
                            <Loader2Icon className="inline animate-spin h-4" /> Saving...
                        </span>
                    ) : isWarn ? 'Apply Warn' : 'Apply Ban'}
                </Button>
            </div>
        </ModalTabInner>
    );
}
